import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';

const AuthContext = createContext(null);
const IDLE_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('verbnotes_token'));
  const [loading, setLoading] = useState(true);

  const {
    isAuthenticated: auth0IsAuthenticated,
    isLoading: auth0IsLoading,
    user: auth0User,
    getIdTokenClaims,
    logout: auth0Logout,
  } = useAuth0();

  // Set axios auth header
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Handle Auth0 login — fetch ID token and sync user
  useEffect(() => {
    const syncAuth0User = async () => {
      if (auth0IsAuthenticated && !auth0IsLoading) {
        try {
          const claims = await getIdTokenClaims();
          const idToken = claims?.__raw;
          if (idToken) {
            localStorage.setItem('verbnotes_token', idToken);
            setToken(idToken);

            axios.defaults.headers.common['Authorization'] = `Bearer ${idToken}`;
            const response = await axios.get(`${API}/auth/me`);
            setUser(response.data);
          }
        } catch (error) {
          console.error('Auth0 sync failed:', error);
        }
        setLoading(false);
      }
    };
    syncAuth0User();
  }, [auth0IsAuthenticated, auth0IsLoading, getIdTokenClaims]);

  // Check legacy JWT on mount
  useEffect(() => {
    const checkAuth = async () => {
      if (auth0IsLoading) return; // Wait for Auth0 to settle
      if (auth0IsAuthenticated) return; // Auth0 handles its own flow

      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`);
          setUser(response.data);
        } catch (error) {
          console.error('Auth check failed:', error);
          legacyLogout();
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, [token, auth0IsLoading, auth0IsAuthenticated]);

  const login = async (email, password, totpCode = null, recoveryCode = null) => {
    const payload = { email, password };
    if (totpCode) payload.totp_code = totpCode;
    if (recoveryCode) payload.recovery_code = recoveryCode;
    const response = await axios.post(`${API}/auth/login`, payload);
    
    if (response.data.requires_2fa) {
      return { requires_2fa: true };
    }
    
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('verbnotes_token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('verbnotes_token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const legacyLogout = useCallback(() => {
    localStorage.removeItem('verbnotes_token');
    setToken(null);
    setUser(null);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('verbnotes_token');
    setToken(null);
    setUser(null);
    if (auth0IsAuthenticated) {
      auth0Logout({ logoutParams: { returnTo: window.location.origin } });
    }
  }, [auth0IsAuthenticated, auth0Logout]);

  // Idle timeout - auto-logout after 15 min inactivity
  const idleTimerRef = useRef(null);

  useEffect(() => {
    if (!token) return;

    const resetTimer = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => {
        logout();
      }, IDLE_TIMEOUT_MS);
    };

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'];
    events.forEach(e => window.addEventListener(e, resetTimer, { passive: true }));
    resetTimer();

    return () => {
      events.forEach(e => window.removeEventListener(e, resetTimer));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [token, logout]);

  const isAuthenticated = !!user;
  const isLoading = loading || auth0IsLoading;

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading: isLoading,
      isAuthenticated,
      login,
      register,
      logout
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
